import { createFileRoute, Link } from "@tanstack/react-router";
import { EsPageShell, EsSection } from "@/components/site/EsPageShell";
import { esHead, esWebPageSchema } from "@/lib/es-pages";
import { EsgBalloonCoverageTable } from "@/components/site/EsgBalloonCoverageTable";
import { FaqList, faqSchema, type Faq } from "@/components/site/InsuranceProse";

const URL = "https://virginiabariatric.com/es/seguro-para-cirugia-bariatrica";

const FAQS: Faq[] = [
  {
    q: "¿Anthem Blue Cross Blue Shield de Virginia cubre la cirugía bariátrica?",
    a: "La mayoría de los planes de Anthem BCBS de Virginia cubren la manga gástrica y el bypass gástrico. Debe cumplir los criterios NIH: IMC de 40 o más, o de 35 o más con una enfermedad relacionada con la obesidad. También debe completar un programa de pérdida de peso supervisado por un médico de tres a seis meses. Algunos planes de empleador excluyen la cirugía bariátrica, por lo que siempre verificamos sus beneficios.",
  },
  {
    q: "¿Medicare cubre la cirugía de pérdida de peso en Virginia?",
    a: "Sí. Medicare cubre la manga gástrica, el bypass gástrico y el switch duodenal. Se necesita un IMC de 35 o más y al menos una enfermedad relacionada con la obesidad. La cirugía debe realizarse en un centro aprobado por Medicare. Medicare no exige dieta supervisada.",
  },
  {
    q: "¿Aetna exige una dieta supervisada?",
    a: "Sí, en la mayoría de los casos. Casi todos los planes comerciales de Aetna exigen 12 o más sesiones documentadas en un programa conductual de pérdida de peso, dentro de los dos años previos a la cirugía. También aplican los criterios de IMC y la evaluación psicológica.",
  },
  {
    q: "¿Cuánto tarda la autorización previa para la cirugía bariátrica?",
    a: "La mayoría de las aseguradoras de Virginia aprueban en unas dos semanas una vez que el expediente está completo. Nuestro consultorio reúne los registros de la dieta supervisada, las evaluaciones psicológica y nutricional y los resultados de laboratorio, y después envía y da seguimiento a la solicitud por usted.",
  },
  {
    q: "¿Qué pasa si mi seguro niega la cobertura de la cirugía bariátrica?",
    a: "Las negaciones en la primera revisión son frecuentes. Nosotros apelamos por usted. Muchas se resuelven con una llamada de médico a médico con el revisor de la aseguradora. Si su plan excluye por completo la cirugía bariátrica, existen precios de pago propio y financiamiento mensual con Prosper® Healthcare Lending.",
  },
  {
    q: "¿El seguro cubre la gastroplastia endoscópica en manga (ESG)?",
    a: "Cada vez más. La ESG recibió un código CPT permanente (43889) vigente desde el 1 de enero de 2026. Aseguradoras grandes como Cigna y Anthem ya la cubren para pacientes que califican, con los mismos criterios de IMC que la manga gástrica. Aetna y UnitedHealthcare todavía la consideran experimental. Medicare y Medicaid de Virginia no la cubren, por lo que verificamos sus beneficios individuales.",
  },
  {
    q: "¿El seguro cubre el balón intragástrico?",
    a: "Normalmente no. El balón intragástrico está aprobado por la FDA, pero casi todos los planes lo tratan como un servicio de control de peso no cubierto, incluidos Medicare y Medicaid de Virginia. En la práctica es un procedimiento de pago propio. Con gusto revisamos sus beneficios por si su plan es una excepción.",
  },
];

export const Route = createFileRoute("/es/seguro-para-cirugia-bariatrica")({
  head: () => {
    const base = esHead("surgeryInsurance");
    return {
      ...base,
      scripts: import.meta.env.SSR
        ? [
            {
              type: "application/ld+json",
              children: JSON.stringify(esWebPageSchema("surgeryInsurance")),
            },
            {
              type: "application/ld+json",
              children: JSON.stringify(faqSchema(FAQS, URL, "es")),
            },
          ]
        : [],
    };
  },
  component: EsSurgeryInsurance,
});

function EsSurgeryInsurance() {
  return (
    <EsPageShell
      page="surgeryInsurance"
      title="Seguro para cirugía bariátrica"
      intro="La mayoría de los planes en Virginia cubre la cirugía bariátrica si usted cumple los criterios médicos habituales. Algunos contratos de empleador la excluyen por completo, por eso verificamos sus beneficios antes de cada consulta. Esto es lo que vemos con más frecuencia, plan por plan."
    >
      <EsSection id="criterios" heading="Criterios médicos estándar">
        <p>
          Casi todas las aseguradoras parten de los mismos criterios. Usted califica con un
          índice de masa corporal (IMC) de 40 o más. También puede calificar con un IMC de 35
          o más junto con al menos una enfermedad relacionada con la obesidad: diabetes tipo 2,
          presión alta (hipertensión), apnea del sueño, enfermedad articular grave o hígado
          graso.
        </p>
        <p>
          Puede consultar la lista completa de requisitos en nuestra{" "}
          <Link to="/es/manga-gastrica" className="font-medium text-brand-logo hover:underline">
            página de manga gástrica
          </Link>{" "}
          o llamarnos para revisar su caso en español.
        </p>
      </EsSection>

      <EsSection id="ley-virginia" heading="La ley de obesidad mórbida de Virginia">
        <p>
          La ley de Virginia exige que los planes de salud de empleadores grandes cubran el
          tratamiento de la obesidad mórbida (Código de Virginia § 38.2-3418.13). La ley
          define la obesidad mórbida como un IMC de 40 o más, o de 35 o más con una condición
          como diabetes tipo 2, hipertensión o apnea del sueño. Los planes deben cubrir esta
          atención en condiciones comparables a las de otros servicios médicos.
        </p>
        <p>
          El mandato no aplica a los planes individuales ni a los de grupos pequeños, cuya
          cobertura varía según la póliza. Por eso revisamos su plan específico en lugar de
          guiarnos por la política general de la aseguradora.
        </p>
      </EsSection>

      <EsSection id="anthem" heading="Anthem Blue Cross Blue Shield de Virginia">
        <p>
          Anthem BCBS de Virginia y HealthKeepers (política CG-SURG-83) cubren la{" "}
          <Link to="/es/manga-gastrica" className="font-medium text-brand-logo hover:underline">
            manga gástrica
          </Link>
          , el{" "}
          <Link to="/es/bypass-gastrico" className="font-medium text-brand-logo hover:underline">
            bypass gástrico Roux-en-Y
          </Link>{" "}
          y la cirugía de revisión en la mayoría de los planes. Los requisitos habituales son:
        </p>
        <ul className="list-disc space-y-2 pl-5">
          <li>Tener 18 años o más.</li>
          <li>Criterios de IMC estándar (40 o más, o 35 o más con una condición que califique).</li>
          <li>Un programa de pérdida de peso supervisado por un médico de tres a seis meses.</li>
          <li>Evaluación psicológica y evaluación nutricional.</li>
        </ul>
      </EsSection>

      <EsSection id="aetna" heading="Aetna">
        <p>
          La mayoría de los planes comerciales de Aetna exigen 12 o más sesiones documentadas
          en un programa conductual de pérdida de peso, realizadas dentro de los dos años
          previos a la cirugía. También se requiere una evaluación psicológica. Aetna
          actualmente considera experimentales la ESG y el balón intragástrico.
        </p>
      </EsSection>

      <EsSection id="cigna" heading="Cigna">
        <p>
          Cigna (política 0051) es una de las aseguradoras más flexibles en Virginia. Cubre la
          manga gástrica, el bypass gástrico y el switch duodenal con un IMC de 35 o más, sin
          exigir otra enfermedad. También los cubre con un IMC de 30 a 34.9 cuando existe una
          enfermedad relacionada con la obesidad.
        </p>
        <p>
          Cigna cubre la ESG como médicamente necesaria bajo los mismos criterios. Los balones
          no están cubiertos.
        </p>
      </EsSection>

      <EsSection id="unitedhealthcare" heading="UnitedHealthcare">
        <p>
          UnitedHealthcare cubre los procedimientos bariátricos estándar. Se necesita un IMC de
          40 o más, o de 35 a 39.9 con al menos una enfermedad relacionada. También se exige
          una evaluación preoperatoria y una valoración psicosocial.
        </p>
        <p>
          Los requisitos de dieta supervisada varían según el plan del empleador: algunos piden
          seis meses y otros ninguno. Por eso la verificación de beneficios es indispensable.
          UnitedHealthcare considera hoy no comprobadas la ESG y los balones.
        </p>
      </EsSection>

      <EsSection id="medicare" heading="Medicare">
        <p>
          Medicare (determinación nacional de cobertura 100.1) cubre la manga gástrica, el
          bypass gástrico Roux-en-Y, el switch duodenal y la banda gástrica ajustable. Se
          necesita un IMC de 35 o más y al menos una enfermedad relacionada con la obesidad,
          además de documentación de que el tratamiento médico de la obesidad no dio resultado.
        </p>
        <p>
          Medicare <em>no</em> exige dieta supervisada, lo que hace la autorización previa
          bastante más rápida. La cirugía debe realizarse en un centro aprobado por Medicare, y
          el nuestro lo es. Medicare no cubre la ESG ni el balón intragástrico.
        </p>
      </EsSection>

      <EsSection id="medicaid" heading="Medicaid de Virginia (Cardinal Care)">
        <p>
          Medicaid de Virginia cubre la cirugía bariátrica médicamente necesaria con
          autorización previa. Los miembros se inscriben a través de planes de atención
          administrada: Anthem HealthKeepers Plus, Aetna Better Health, Sentara Community Plan
          y UnitedHealthcare Community Plan.
        </p>
        <p>
          Cada plan hace su propia revisión de necesidad médica. La mayoría exige un IMC de 40
          o más, o de 35 o más con una enfermedad grave relacionada con la obesidad, además de
          documentación de que el tratamiento no quirúrgico falló. La ESG y el balón
          generalmente no están cubiertos.
        </p>
      </EsSection>

      <EsSection id="esg-balon" heading="Cobertura de la ESG y del balón intragástrico" maxWidth="7xl">
        <p>
          La cobertura de la gastroplastia endoscópica en manga (ESG) está mejorando rápido:
          recibió un código CPT permanente (43889) vigente desde el 1 de enero de 2026 y varias
          aseguradoras grandes, como Cigna y Anthem, ya la cubren con los mismos criterios de
          IMC que la manga gástrica. Aetna y UnitedHealthcare todavía la clasifican como
          experimental, y Medicare y Medicaid de Virginia no la cubren.
        </p>
        <p>
          El{" "}
          <Link to="/es/balon-gastrico" className="font-medium text-brand-logo hover:underline">
            balón intragástrico
          </Link>{" "}
          está aprobado por la FDA, pero casi todos los planes lo tratan como un servicio de
          control de peso no cubierto. En la práctica es un procedimiento de pago propio.
        </p>
        <EsgBalloonCoverageTable />
      </EsSection>

      <EsSection id="pago-propio" heading="Pago propio y financiamiento">
        <p>
          ¿Su plan excluye la cirugía bariátrica, o prefiere avanzar más rápido que los plazos
          que exige el seguro? El pago propio es una alternativa, con precios de paquete
          claros. También trabajamos con Prosper® Healthcare Lending para el financiamiento en
          cuotas mensuales.
        </p>
        <p>
          <Link
            to="/es/precios-y-financiamiento"
            className="font-medium text-brand-logo hover:underline"
          >
            Vea las opciones de precios y financiamiento en español
          </Link>
          .
        </p>
      </EsSection>

      <EsSection id="autorizacion" heading="Cómo manejamos la autorización previa">
        <p>
          Verificamos la cobertura de su plan desde el inicio. Si su plan excluye la cirugía,
          usted merece saberlo antes de invertir tiempo y energía en la preparación.
        </p>
        <p>
          Una vez que hay cobertura o un plan de pago, nosotros nos encargamos del papeleo:
          llevamos el registro de sus visitas de dieta supervisada, reunimos las evaluaciones
          psicológica y nutricional y los resultados de laboratorio, y enviamos el expediente
          completo por usted. La mayoría de las aprobaciones llega en unas dos semanas.
        </p>
        <p>
          Cuando una aseguradora niega la solicitud, con frecuencia por motivos poco claros,
          iniciamos la apelación por usted. Muchas negaciones se resuelven con una llamada de
          médico a médico; otras requieren el proceso formal de apelación.
        </p>
      </EsSection>

      <EsSection id="preguntas" heading="Preguntas frecuentes sobre el seguro para cirugía">
        <FaqList faqs={FAQS} />
        <p className="text-sm text-muted-foreground">
          ¿Busca cobertura para medicamentos GLP-1?{" "}
          <Link to="/es/cobertura-de-seguro" className="font-medium text-brand-logo hover:underline">
            Vea la guía de cobertura de medicamentos
          </Link>
          .
        </p>
      </EsSection>
    </EsPageShell>
  );
}
